import { useEffect, useRef } from 'react'
import styles from './pursuit.module.css'

/**
 * The checkbox column shared by every table view (Pursuit Stages, Worklist,
 * Gaps). Two cells and nothing else — the state lives in `useRowSelection`, so
 * no view owns a private copy of what is selected.
 *
 * `rows` on the header is the FULL list that header governs (the whole scoped
 * view / bucket), never the rendered page: select-all means all of it, exactly
 * like the CSV export means the full filtered set rather than the visible page.
 */

/** Header cell: select-all / select-none over `rows`, tri-state when partial. */
export function SelectAllTh({ rows = [], selection, label = 'rows in this view' }) {
  const ref = useRef(null)
  const on = selection ? selection.countIn(rows) : 0
  const all = rows.length > 0 && on === rows.length
  const some = on > 0 && !all

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some
  }, [some])

  if (!selection) return null
  return (
    <th className={styles.selHead}>
      {/* Same 44px hit area as the row cell below — see SelectCell. Select-all
          governs the whole scoped view, so it is the one tick in the column
          with the largest consequence and the worst thing to mis-tap. */}
      <label className={styles.rowCheckHit}>
        <input
          ref={ref}
          type="checkbox"
          className={styles.rowCheck}
          checked={all}
          disabled={!rows.length}
          onChange={() => selection.setMany(rows, !all)}
          title={all ? `Deselect all ${label}` : `Select all ${rows.length.toLocaleString()} ${label}`}
          aria-label={all ? `Deselect all ${label}` : `Select all ${label}`}
        />
      </label>
    </th>
  )
}

/** Row cell: ticks one record. Never opens the drawer the row click opens.
 *
 * 🔴 THE CHECKBOX IS WRAPPED IN A LABEL, AND THAT IS A 982-TARGET FIX RATHER
 * THAN MARKUP TIDYING.
 *
 * Censused on the live 2.120.0 bundle at 390px, measured 2026-10-17: the
 * Command Center renders 10,878 interactive elements of which 5,845 are tap
 * targets under 44px, and 982 of them — the single largest group,
 * 16.8% — are this input. It measures 19px inside a cell that is already 44px
 * tall, so the thumb target is the 19px glyph and the 25px of cell around it
 * does nothing. The cell cannot be made the target either: it carries
 * `stopPropagation` to keep a tick from opening the drawer, so a click on the
 * padding is swallowed rather than passed to the input.
 *
 * ⚠️ AND PADDING ON THE INPUT WOULD NOT HAVE WORKED. A checkbox is a replaced
 * control: `padding` is ignored and `height` scales the glyph itself, so the
 * two obvious CSS fixes either do nothing or render 982 oversized checkboxes.
 * A label is the one construct that grows the hit area while leaving the box
 * at 14px — and it is better markup regardless, because a labelled control is
 * a nameable one.
 *
 * ⚠️ IT IS ALSO EXACTLY WHAT THE PROBE MEASURES. mobile-ui-probe.mjs climbs an
 * input to its labelling ancestor before measuring, on the stated grounds that
 * "a 20px checkbox inside a 44px <label> is a 44px target". Before this change
 * there was no label to climb to, so the probe correctly measured the glyph.
 * The number moving is the thumb target moving, not the measurement being
 * dodged. */
export function SelectCell({ item, selection }) {
  if (!selection) return null
  const on = selection.isSelected(item.id)
  return (
    <td className={styles.selCell} data-col="select" onClick={(e) => e.stopPropagation()}>
      <label className={styles.rowCheckHit}>
        <input
          type="checkbox"
          className={styles.rowCheck}
          checked={on}
          onChange={() => selection.toggle(item.id)}
          aria-label={`${on ? 'Deselect' : 'Select'} ${item.name || 'record'}`}
        />
      </label>
    </td>
  )
}
